import { formatRelative } from "date-fns";
import { de } from "date-fns/locale";
import { Fragment, memo } from "react";

import type { InvoiceComment } from "@/apis/gql/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TypographyMuted, TypographySmall } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

const ENABLE_AVATAR = false;

interface CommentProps {
  comment: InvoiceComment;
  self: boolean;
}

const _Comment = ({ comment, self }: CommentProps) => {
  const formattedDate = formatRelative(
    new Date(comment.createdAt),
    new Date(),
    {
      locale: de,
    },
  );

  return (
    <Card
      className={cn(
        "max-w-[85%] py-0.5 px-3 pr-12",
        !self && "mr-auto",
        self && "ml-auto bg-primary/10",
      )}
    >
      <CardHeader
        className={cn(
          "flex flex-row items-center gap-4 px-0 py-1.5",
          self && "flex-row-reverse",
        )}
      >
        <div
          className={cn(
            "flex flex-row items-center gap-1",
            self && "items-center",
          )}
        >
          {comment.user.image && ENABLE_AVATAR && (
            <Avatar className="h-8 w-8 bg-white">
              <AvatarImage
                src={comment.user.image}
                referrerPolicy="no-referrer"
              />
              <AvatarFallback>
                {comment.user.name
                  .split(" ")
                  .map((name) => name[0])
                  .join("")}
              </AvatarFallback>
            </Avatar>
          )}
          <TypographySmall className="font-medium">
            {comment.user.name}
          </TypographySmall>
          <TypographyMuted className="text-xs">{formattedDate}</TypographyMuted>
        </div>
      </CardHeader>
      <CardContent className="py-1 px-0 pt-0">
        <p className="text-sm whitespace-pre-wrap">
          {formatComment(comment.comment)}
        </p>
      </CardContent>
    </Card>
  );
};

export const Comment = memo(_Comment);

const formatComment = (text: string) => {
  const mentionRegex = /@\[(.*?)\]\((.*?)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  // biome-ignore lint/suspicious/noAssignInExpressions: not necessary
  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const [_, display] = match;
    parts.push(
      <span
        key={match.index}
        className="bg-primary/10 text-primary font-medium rounded-sm px-1 py-0.5"
      >
        @{display}
      </span>,
    );

    lastIndex = mentionRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.map((part, i) =>
    typeof part === "string" ? <Fragment key={i}>{part}</Fragment> : part,
  );
};
